import { DescMessage, MessageShape } from "@bufbuild/protobuf";
import { ConnectError } from "@connectrpc/connect";
import { useQuery } from "@connectrpc/connect-query";
import { UseQueryResult } from "@tanstack/react-query";
import { usePersistedGrpcQuery } from "@/utils/connect-query/query-cache-persistence/use-persisted-grpc-query";
import { usePrevious } from "@/utils/hooks/use-previous-value";

export type UseGRPCQueryResult<O extends DescMessage> = UseQueryResult<
  MessageShape<O>,
  ConnectError
>;

export const useGRPCQuery = <I extends DescMessage, O extends DescMessage>(
  method: Parameters<typeof useQuery<I, O>>[0],
  inputs?: Parameters<typeof useQuery<I, O>>[1],
  options?: Parameters<typeof useQuery<I, O>>[2] & { onError?: (err: ConnectError) => void },
): UseGRPCQueryResult<O> => {
  const query = usePersistedGrpcQuery(method, inputs, { refetchOnMount: "always", ...options });

  const lastError = usePrevious(query.error);
  if (options?.onError != null && lastError !== query.error && query.error != null) {
    options.onError(query.error);
  }

  return query;
};
